import Vue from 'vue'
import { getValidStandardSets, getItem } from '@/api/pathways'
import { getSuperPage } from '@/api/superpages'
import { getGoalByUuid, getGoalBySlug, getStaircaseForContent, getGoalCustomization, getSuperPageCustomization } from '@/api/goaldetail'
import { getStrategiesForUuids } from '@/api/strategies'
import { getToolkitStandards } from '@/api/toolkitstandards'
import { getResources } from '@/api/pathways'
// Aliased: this action has the same name as the API call it makes, and an object-literal
// method name creates no binding, so the unaliased import would read as a recursive call.
import {
  getGeneratedAssessments,
  getGenerateAccess,
  deleteGeneratedAssessment as requestAssessmentDelete
} from '@/api/generatedassessments'
import {
  blurContentPermissions,
  actionPermissions,
  CONTENT_TYPES,
  ACTION_TYPES,
} from '@/util/access_visibility'
import { isGoalPremium } from '@/util/goal_scores'
import { hasNonEnglishLang } from '@/util/resource_languages'
import { CUSTOMIZATIONS } from './customizationdemo.catalog'

export default {
  namespaced: true,
  state: {
    fetching: false,
    fetchingCustomization: false,
    customizationError: null,
    lastPageRequest: null,
    content: {},
    customizedGoal: null,
    fetchedStandardsBySetCode: {},
    staircaseContent: [],
    strategies: [],
    items: {}, // uuid -> item document
    validStandardSets: null,
    saveToLibraryStatus: {
      savedErrorMessage: null,
      savedSuccessInfo: {
        name: null,
        uuid: null,
      },
    },
    forbidden: null,
    error: null,
    resources: {}, // Map of objectId -> resources array
    selectedLanguage: {}, // Map of objectId -> selected language
    resourcesFetching: {}, // Map of objectId -> fetching status
    generatedAssessments: {}, // Map of saved page uuid -> target groups array (server order)
    generateAccess: {}, // Map of saved page uuid -> may this requester generate here
    autoReviewCompleted: false, // Track if auto-review has been run for current goal
  },
  mutations: {
    pageContentChanged(state) {
      state.strategies = []
      state.fetchedStandardsBySetCode = {}
      state.staircaseContent = []
      state.items = {}
      state.saveToLibraryStatus = {
        savedErrorMessage: null,
        savedSuccessInfo: {
          name: null,
          uuid: null,
        },
      }
      state.customizedGoal = null
      state.autoReviewCompleted = false
    },
    clearContent(state) {
      state.content = {}
      state.fetchingCustomization = false
      state.customizationError = null
      state.customizedGoal = null
      state.strategies = []
      state.fetchedStandardsBySetCode = {}
      state.staircaseContent = []
      state.items = {}
      state.resources = {}
      state.selectedLanguage = {}
      state.resourcesFetching = {}
      state.generatedAssessments = {}
      state.autoReviewCompleted = false
    },
    setFetching(state, flag) {
      state.fetching = flag
    },
    setFetchingCustomization(state, flag) {
      state.fetchingCustomization = flag
    },
    setCustomizationError(state, error) {
      state.customizationError = error
    },
    setCustomizedGoal(state, flag) {
      state.customizedGoal = flag
    },
    setPageContent(state, pageContent) {
      let contentCopy = pageContent
      if (state.content === pageContent) {
        // do a clone to create new object references
        // in order to guarantee reactive updates
        contentCopy = JSON.parse(JSON.stringify(pageContent))
      }
      state.content = contentCopy
    },
    applyCustomization(state, customizedContent) {
      if (customizedContent.goal_text) {
        Vue.set(state.content, 'goal_text', customizedContent.goal_text)
      }
      if (customizedContent.objectives && state.content.objectives) {
        customizedContent.objectives.forEach(customizedObj => {
          const existingObj = state.content.objectives.find(o => o.level === customizedObj.level)
          if (existingObj && customizedObj.text) {
            Vue.set(existingObj, 'text', customizedObj.text)
          }
        })
      }
      if (customizedContent.customized !== undefined) {
        Vue.set(state.content, 'customized', customizedContent.customized)
      }
    },
    setFetchedStandardsForSetCode(state, { setCode, standards }) {
      Vue.set(state.fetchedStandardsBySetCode, setCode, standards)
    },
    setStaircaseContent(state, staircaseContent) {
      state.staircaseContent = staircaseContent
    },
    setPageStrategies(state, strategiesArr) {
      state.strategies = strategiesArr
    },
    addItem(state, item) {
      let itemToAugment = {}
      itemToAugment[item.uuid] = item
      state.items = Object.assign({}, state.items, itemToAugment)
    },
    setValidStandardSets(state, standardSetCodes) {
      state.validStandardSets = standardSetCodes
    },
    setForbidden(state, forbidden) {
      state.forbidden = forbidden
    },
    setError(state, error) {
      state.error = error
    },
    // Expect payload to have name and uuid fields
    setSaveToLibrarySuccess(state, payload) {
      state.saveToLibraryStatus.savedSuccessInfo.name = payload.name
      state.saveToLibraryStatus.savedSuccessInfo.uuid = payload.uuid
    },
    setSaveToLibraryError(state, errorMessage) {
      state.saveToLibraryStatus.savedErrorMessage = errorMessage
    },
    resetSaveStatus(state) {
      state.saveToLibraryStatus = {
        savedErrorMessage: null,
        savedSuccessInfo: {
          name: null,
          uuid: null,
          libraryContentUrl: null,
        },
      }
    },
    setResources(state, { contentId, resources }) {
      // Make sure we're creating a new array reference to ensure reactivity
      Vue.set(state.resources, contentId, [...resources])
    },
    setResourcesFetching(state, { contentId, fetching }) {
      Vue.set(state.resourcesFetching, contentId, fetching)
    },
    setSelectedLanguage(state, { contentId, language }) {
      Vue.set(state.selectedLanguage, contentId, language)
    },
    setGeneratedAssessments(state, { savedPageUuid, targets }) {
      // New array reference so the tile strip re-renders, same as setResources.
      Vue.set(state.generatedAssessments, savedPageUuid, [...targets])
    },
    // Drops a group that empties, rather than leaving it behind with no assessments.
    // That mirrors the server, which groups only live records and so never sends an
    // empty group -- leaving one here would make the client's shape diverge from the
    // next fetch's, and `hasAnnualGeneratedAssessments` reads the group's length rather
    // than the group's presence, so the two would answer differently.
    removeGeneratedAssessment(state, { savedPageUuid, uuid }) {
      const targets = state.generatedAssessments[savedPageUuid]
      if (!targets) return

      const remaining = targets
        .map((group) => ({
          ...group,
          assessments: (group.assessments || []).filter((a) => a.uuid !== uuid)
        }))
        .filter((group) => group.assessments.length > 0)

      // New array reference for the same reason setGeneratedAssessments makes one.
      Vue.set(state.generatedAssessments, savedPageUuid, remaining)
    },
    setGenerateAccess(state, { savedPageUuid, allowed }) {
      Vue.set(state.generateAccess, savedPageUuid, allowed)
    },
    setAutoReviewCompleted(state, completed) {
      state.autoReviewCompleted = completed
    },
    setLastPageRequest(state, pageRequest) {
      state.lastPageRequest = pageRequest
    }
  },
  actions: {
    // lastPageRequest is a snapshot of how the page was loaded, but Beta is a live toggle a
    // presenter flips on the way to starting a demo. Replaying the captured flag would ask for a
    // beta-only customization with beta_mode false, which the backend answers by declining to
    // customize at all, so the page silently redraws unchanged. Requests that never carried the
    // flag, such as public share pages, are left as they were rather than opted in by a replay.
    async refetchCurrentPage(context) {
      const request = context.state.lastPageRequest
      if (!request) return

      const params = 'beta_mode' in request.params
        ? { ...request.params, beta_mode: !!context.rootGetters['visibility/betaVisibility'] }
        : request.params

      await context.dispatch('fetchPageContent', { ...request, params })
    },
    // payload should have the following fields:
    // - type: CONTENT_TYPES.GOAL or CONTENT_TYPES.SUPER_PAGE
    // - attribute: uuid or slug
    // - value: the value corresponding to the attribute
    async fetchPageContent(context, payload) {
      let fetchFunc = null
      if (payload.type === CONTENT_TYPES.GOAL) {
        if (payload.attribute === 'uuid') {
          fetchFunc = getGoalByUuid
        } else {
          fetchFunc = getGoalBySlug
        }
      } else if (payload.type === CONTENT_TYPES.SUPER_PAGE) {
        if (payload.attribute === 'uuid') {
          fetchFunc = getSuperPage
        } else {
          // no op
          console.warn('Fetching anchor page by anything other than uuid is not supported')
        }
      }
      context.commit('setFetching', true)

      // `retried` is this action's own retry bookkeeping, mutated onto the payload before a
      // retry. Storing it would make a later demo refetch treat its first failure as a second and
      // skip the retry, so the remembered request is the caller's request only.
      const { retried: _retried, ...pageRequest } = payload
      context.commit('setLastPageRequest', { ...pageRequest, params: { ...pageRequest.params } })

      // Add skip_customization to params if content_profile is provided
      // this allows us to fetch the base content without customization
      // for a better loading experience since customizing takes time
      const params = { ...payload.params }
      const demoProfileId = context.rootGetters['customizationDemo/demoProfileFor']?.(CUSTOMIZATIONS.GOALS)
      if (demoProfileId) {
        params.content_profile = demoProfileId
      }
      if (params.content_profile) {
        params.skip_customization = 'true'
      }

      try {
        let goaldetail = await fetchFunc(payload.value, params)
        context.commit('setPageContent', goaldetail)
        context.commit('pageContentChanged')
        context.dispatch('fetchStrategiesForUuids', goaldetail.adaptation_strategies)
        context.commit('setFetching', false)

        // Phase 2: Fetch customization if needed
        if (goaldetail.should_customize && params.content_profile) {
          context.commit('setFetchingCustomization', true)
          context.commit('setCustomizedGoal', true)
          context.dispatch('fetchCustomization', {
            type: payload.type,
            uuid: goaldetail.uuid,
            contentProfile: params.content_profile,
            betaMode: params.beta_mode
          })
        } else if (params.content_profile) {
          context.commit('setFetchingCustomization', false)
        }

        return goaldetail
      } catch ({ status }) {
        if (status === 403) {
          context.commit('setForbidden', true)
          context.commit('setFetching', false)
          context.commit('setFetchingCustomization', false)
        } else {
          if (payload.retried) {
            context.commit('setError', true)
            context.commit('setFetching', false)
            context.commit('setFetchingCustomization', false)
          } else {
            payload.retried = true
            context.commit('setFetching', false)
            context.dispatch('fetchPageContent', payload)
          }
        }
      }
    },
    async fetchCustomization(context, { type, uuid, contentProfile, betaMode }) {
      context.commit('setFetchingCustomization', true)
      context.commit('setCustomizationError', null)

      try {
        const fetchFunc = type === CONTENT_TYPES.SUPER_PAGE ? getSuperPageCustomization : getGoalCustomization
        const customizedContent = await fetchFunc(uuid, contentProfile, betaMode)
        if (context.state.content && context.state.content.uuid === uuid) {
          context.commit('applyCustomization', customizedContent)
        }
      } catch (error) {
        console.error('Failed to fetch customization:', error)
        context.commit('setCustomizationError', error)
      } finally {
        if (context.state.content && context.state.content.uuid === uuid) {
          context.commit('setFetchingCustomization', false)
        }
      }
    },
    fetchPageStandardsForSets(context, { contentStandardSetRefs, standardSets }) {
      let standardSetRefs = context.state.content.standard_set_refs
      let fetchPromises = []

      standardSets.forEach((setCode) => {
        let standardRefsForSetCode = standardSetRefs
          .filter((setRef) => setRef.standard_set_code === setCode)
          .map((setRef) => setRef.standard_long_ref)

        if (standardRefsForSetCode.length > 0) {
          let fetchStandardsPromise = getToolkitStandards(setCode, standardRefsForSetCode, {
            substandards: true,
          }).then((results) => {
            let standards = results.standards.filter((standard) => {
              return !!standard
            })
            context.commit('setFetchedStandardsForSetCode', {
              setCode,
              standards,
            })
          })
          fetchPromises.push(fetchStandardsPromise)
        }
      })

      return Promise.all(fetchPromises)
    },
    async fetchPageStaircaseContentForSet(
      { state, commit },
      { contentUuid, staircaseStandardSetCode, subject }
    ) {
      if (!staircaseStandardSetCode) {
        return []
      }
      let staircaseContent = await getStaircaseForContent(
        contentUuid,
        staircaseStandardSetCode,
        subject
      )
      commit('setStaircaseContent', staircaseContent)
      return staircaseContent
    },
    fetchStrategiesForUuids(context, uuids) {
      return getStrategiesForUuids(uuids)
        .then((strategiesArr) => {
          context.commit('setPageStrategies', strategiesArr)
          return strategiesArr
        })
        .catch((err) => {
          console.dir(err)
        })
    },
    fetchItem(context, itemId) {
      if (context.state.items[itemId]) {
        return new Promise((resolve) => resolve(context.state.items[itemId]))
      } else {
        return getItem(itemId).then((item) => {
          context.commit('addItem', item)
          return item
        })
      }
    },
    async fetchValidStandardSets(context) {
      if (!context.state.validStandardSets) {
        let standardSetCodes = await getValidStandardSets()
        context.commit('setValidStandardSets', standardSetCodes)
        return standardSetCodes
      } else {
        return context.state.validStandardSets
      }
    },
    async fetchResources({ commit, state }, { contentId, resourceUuids }) {
      if (!resourceUuids || resourceUuids.length === 0) {
        commit('setResources', { contentId, resources: [] })
        return []
      }

      if (state.resources[contentId] &&
          state.resources[contentId].length === resourceUuids.length &&
          state.resources[contentId].every(r => resourceUuids.includes(r.uuid))) {
        return state.resources[contentId]
      }

      try {
        commit('setResourcesFetching', { contentId, fetching: true })
        const resources = await getResources(resourceUuids.join(','))
        commit('setResources', { contentId, resources: [...resources] })

        if (resources.length > 0 && !state.selectedLanguage[contentId]) {
          const languages = [...new Set(resources.map(r => r.language).filter(Boolean))]
          if (languages.length > 0) {
            if (languages.includes('english')) {
              commit('setSelectedLanguage', { contentId, language: 'english' })
            } else {
              commit('setSelectedLanguage', { contentId, language: languages[0] })
            }
          }
        }

        return resources
      } catch (error) {
        console.error('Error fetching resources:', error)
        return []
      } finally {
        commit('setResourcesFetching', { contentId, fetching: false })
      }
    },
    async fetchAllResources({ state, dispatch }) {
      if (state.content.goal_resources && state.content.goal_resources.length > 0) {
        await dispatch('fetchResources', {
          contentId: state.content.uuid,
          resourceUuids: state.content.goal_resources
        })
      }

      if (state.content.objectives) {
        await Promise.all(
          state.content.objectives
            .filter(objective => objective.resources && objective.resources.length > 0)
            .map(async objective => {
              return await dispatch('fetchResources', {
                contentId: objective.uuid,
                resourceUuids: objective.resources
              })
            })
        )
      }
    },
    async fetchGeneratedAssessments({ commit }, { savedPageUuid }) {
      if (!savedPageUuid) {
        return []
      }

      try {
        const response = await getGeneratedAssessments(savedPageUuid)
        const targets = (response && response.targets) || []
        commit('setGeneratedAssessments', { savedPageUuid, targets })
        return targets
      } catch (error) {
        // The endpoint answers every refusal -- and nonexistence -- with a 404, so a 404
        // means "nothing to show here", not a failure worth surfacing.
        if (error && error.status === 404) {
          commit('setGeneratedAssessments', { savedPageUuid, targets: [] })
          return []
        }
        throw error
      }
    },
    // Rethrows, unlike fetchGenerateAccess below: the caller opened a confirmation
    // dialog and the teacher pressed Delete, so a failure has to reach them. It also
    // decides the `Deleted Generated Assessment` outcome, which is why this action does
    // NOT send that event -- the event is specced with `schema_key`, which lives on the
    // tile, and firing here would report success for a request that only got as far as
    // being made.
    //
    // The row is removed from the strip only after the server has confirmed, so a
    // refused delete leaves the tile where it was rather than hiding a record that is
    // still there.
    async deleteGeneratedAssessment({ commit }, { savedPageUuid, uuid }) {
      await requestAssessmentDelete(uuid)
      commit('removeGeneratedAssessment', { savedPageUuid, uuid })
    },
    // Swallows every failure into `false` instead of rethrowing, unlike
    // fetchGeneratedAssessments above. This action's only consumer is the visibility of
    // an entry point, and the server re-checks the same decision on the generate call --
    // so a failure here costs a hidden button, while a thrown error from a page-load
    // fetch would break rendering over a control the teacher may not even want.
    async fetchGenerateAccess({ commit }, { savedPageUuid }) {
      if (!savedPageUuid) {
        return false
      }

      try {
        const response = await getGenerateAccess(savedPageUuid)
        const allowed = !!(response && response.allowed)
        commit('setGenerateAccess', { savedPageUuid, allowed })
        return allowed
      } catch (error) {
        commit('setGenerateAccess', { savedPageUuid, allowed: false })
        return false
      }
    }
  },
  getters: {
    isAnchorPage(state) {
      return state.content.super_page
    },
    itemById(state) {
      return (itemId) => state.items[itemId]
    },
    customizedGoal(state) {
      return state.customizedGoal
    },
    strategyMap(state) {
      let strategyMap = {}
      state.strategies.forEach((strategy) => {
        if (strategy.published === true) {
          strategyMap[strategy.title.toLowerCase()] = strategy
        }
      })
      return strategyMap
    },
    visibleGroupedStandardsBySetCode(state, getters, rootState, rootGetters) {
      if (!state.content.subject || !state.content.standard_set_refs) {
        return []
      }
      let orderedStandardsBySet = []
      let fetchedStandardsBySetCode = state.fetchedStandardsBySetCode
      const distinctVisibleStandardSets = new Set()
      state.content.subject_alignments &&
        state.content.subject_alignments.forEach((sa) => {
          const standardSetsForSubject = rootGetters[
            'contentProfile/visibleStandardSetsForSubject'
          ](sa.subject, {})
          standardSetsForSubject.forEach((s) => distinctVisibleStandardSets.add(s))
        })

      distinctVisibleStandardSets.forEach((setCode) => {
        if (fetchedStandardsBySetCode[setCode] && fetchedStandardsBySetCode[setCode].length > 0) {
          orderedStandardsBySet.push({
            standardSetCode: setCode,
            standards: fetchedStandardsBySetCode[setCode],
          })
        }
      })

      return orderedStandardsBySet
    },
    udlAdaptations(state) {
      return (
        (state.content.udl_adaptations && [
          {
            type: 'engagement',
            adaptations: state.content.udl_adaptations.filter((adaptation) => {
              return adaptation.udl_standard_short_ref.indexOf('UDL III ') > -1
            }),
          },
          {
            type: 'representation',
            adaptations: state.content.udl_adaptations.filter((adaptation) => {
              return adaptation.udl_standard_short_ref.indexOf('UDL I ') > -1
            }),
          },
          {
            type: 'action & expression',
            adaptations: state.content.udl_adaptations.filter((adaptation) => {
              return adaptation.udl_standard_short_ref.indexOf('UDL II ') > -1
            }),
          },
        ]) ||
        []
      )
    },
    accessInfo(state, getters, rootState, rootGetters) {
      return blurContentPermissions(
        getters.isAnchorPage ? CONTENT_TYPES.SUPER_PAGE : CONTENT_TYPES.GOAL,
        state.content,
        rootGetters['user/authInfo']
      )
    },
    actionPermissions(state, getters, rootState, rootGetters) {
      return {
        save: actionPermissions(
          ACTION_TYPES.SAVE,
          getters.isAnchorPage ? CONTENT_TYPES.SUPER_PAGE : CONTENT_TYPES.GOAL,
          state.content,
          rootGetters['user/authInfo']
        ),
        printAssessment: actionPermissions(
          ACTION_TYPES.PRINT,
          CONTENT_TYPES.SUPER_PAGE_ASSESSMENT,
          state.content,
          rootGetters['user/authInfo'],
          rootGetters['contentAccess/contentAccessStatus']
        ),
        printSavedAnchor: actionPermissions(
          ACTION_TYPES.PRINT,
          CONTENT_TYPES.SAVED_SUPER_PAGE,
          state.content,
          rootGetters['user/authInfo'],
          rootGetters['contentAccess/contentAccessStatus']
        ),
        printSavedGoal: actionPermissions(
          ACTION_TYPES.PRINT,
          CONTENT_TYPES.SAVED_GOAL,
          state.content,
          rootGetters['user/authInfo'],
          rootGetters['contentAccess/contentAccessStatus']
        ),
        // SAVED_SUPER_PAGE, the same content type printSavedAnchor uses: generation is
        // offered from the teacher's own saved anchor page, not from library content.
        generateAssessment: actionPermissions(
          ACTION_TYPES.GENERATE_ASSESSMENT,
          CONTENT_TYPES.SAVED_SUPER_PAGE,
          state.content,
          rootGetters['user/authInfo']
        ),
        shareSavedGoal: actionPermissions(
          ACTION_TYPES.SHARE,
          CONTENT_TYPES.SAVED_GOAL,
          state.content,
          rootGetters['user/authInfo'],
          rootGetters['contentAccess/contentAccessStatus']
        ),
      }
    },
    getPMAs(state) {
      return state?.content?.goal_resources || []
    },
    premiumBlock({ forbidden, content }, { isAnchorPage }, rootState, rootGetters) {
      const goalIsPremium = isGoalPremium({ super_page: isAnchorPage, score: content.score })
      return forbidden || (goalIsPremium && !rootGetters['user/authInfo'].isPremium)
    },
    goalLoadError(state) {
      return state.error || false
    },
    resourcesById: (state) => (contentId) => {
      return state.resources[contentId] || []
    },
    resourcesFetchingById: (state) => (contentId) => {
      return state.resourcesFetching[contentId] || false
    },
    selectedLanguageById: (state) => (contentId) => {
      return state.selectedLanguage[contentId] || ''
    },
    languagesById: (state) => (contentId) => {
      const resources = state.resources[contentId] || []
      return [...new Set(resources.map(r => r.language).filter(Boolean))].sort()
    },
    hasNonEnglishLangById: (state) => (contentId) => {
      const resources = state.resources[contentId] || []
      return hasNonEnglishLang(resources)
    },
    filteredResourcesById: (state, getters) => (contentId) => {
      const resources = state.resources[contentId] || []
      const selectedLanguage = getters.selectedLanguageById(contentId)

      if (!selectedLanguage) {
        return resources
      }

      return resources.filter(r => r.language === selectedLanguage || !r.language)
    },
    // The server owns both the grouping and the ordering (annual target first, then
    // objectives by level, OLDEST first within a group so the newest lands at the end of
    // the strip beside the generate tile), so the client only indexes into what it was
    // given -- rebuilding or re-sorting here is how the two would drift.
    generatedAssessmentTargetsById: (state) => (savedPageUuid) => {
      return state.generatedAssessments[savedPageUuid] || []
    },
    // Whether this page has been fetched, which is not the same question as whether
    // it has any assessments -- an empty result and a 404 both cache as []. A caller
    // deciding whether to fetch has to ask this rather than check the array's length,
    // or a page that genuinely has none is refetched every time it asks.
    generatedAssessmentsLoadedById: (state) => (savedPageUuid) => {
      return Object.prototype.hasOwnProperty.call(state.generatedAssessments, savedPageUuid)
    },
    annualTargetGeneratedAssessmentsById: (state, getters) => (savedPageUuid) => {
      return (
        getters
          .generatedAssessmentTargetsById(savedPageUuid)
          .find((group) => group.target_type === 'goal') || null
      )
    },
    // Keyed by the objective level string ('1st'..'4th'), which is all this payload
    // carries. The accordion renderer keys by objective uuid; the store has no
    // uuid-to-level map, so the integrating component passes the level it already has.
    objectiveGeneratedAssessmentsById: (state, getters) => (savedPageUuid, objectiveLevel) => {
      return (
        getters
          .generatedAssessmentTargetsById(savedPageUuid)
          .find(
            (group) => group.target_type === 'objective' && group.target_level === objectiveLevel
          ) || null
      )
    },
    // Defaults to false for a page with no fetched decision, so the entry point is
    // absent until the server has actually said yes -- never present-then-withdrawn.
    generateAllowedById: (state) => (savedPageUuid) => {
      return state.generateAccess[savedPageUuid] === true
    },
    autoReviewCompleted(state) {
      return state.autoReviewCompleted
    },
  },
}
